import React, { useRef, useState } from 'react';
import { generatePassword } from './passwordGenerator';
import { encryptSecret, secretLink } from './fortisecureShare';

const labels = {lower:'Minuscules (a–z)', upper:'Majuscules (A–Z)', digits:'Chiffres (0–9)', symbols:'Symboles (!@#…)', excludeSimilar:'Éviter les caractères similaires (I, l, 1, O, 0, o)'};
export default function PasswordTool() {
  const [length, setLength] = useState(20);
  const [options, setOptions] = useState({lower:true, upper:true, digits:true, symbols:true, excludeSimilar:false});
  const [password, setPassword] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [copying, setCopying] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [duration, setDuration] = useState(8);
  const [views, setViews] = useState(2);
  const [shareUrl, setShareUrl] = useState('');
  const busy = copying || sharing;
  const linkOutput = useRef(null);
  const shareLock = useRef(false);
  const output = useRef(null);
  const clear = () => { setPassword(''); setMessage(''); setError(''); setShareUrl(''); };
  const generate = () => {
    clear();
    try { setPassword(generatePassword(Number(length), options)); }
    catch (failure) { setError(failure.message); }
  };
  const copy = async () => {
    setCopying(true); setMessage('');
    try {
      if (!navigator.clipboard?.writeText) throw new Error('clipboard');
      await navigator.clipboard.writeText(password);
      setMessage('Mot de passe copié.');
    } catch {
      output.current?.focus(); output.current?.select();
      setMessage('Copie automatique indisponible. Le mot de passe est sélectionné : utilisez Ctrl+C ou Cmd+C.');
    } finally { setCopying(false); }
  };
  const resetShare = () => { setShareUrl(''); setMessage(''); setError(''); };
  const share = async () => {
    if (!password || shareLock.current || shareUrl) return;
    shareLock.current = true; setSharing(true); setError(''); setMessage('');
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 25000);
    try {
      const user = JSON.parse(localStorage.getItem('am_user') || '{}');
      const token = localStorage.getItem('am_token') || user?.token || '';
      if (!token) throw new Error('Reconnectez-vous au CMS pour partager un mot de passe.');
      const encrypted = await encryptSecret(password);
      const response = await fetch('https://cms.amservices.eu/api/fortisecure_push.php', {
        method:'POST', headers:{'Content-Type':'application/json', 'X-Auth-Token':token},
        body:JSON.stringify({envelope:encrypted.envelope, duration, views:Number(views)}), signal:controller.signal,
      });
      let data;
      try { data = await response.json(); } catch { throw new Error('Réponse du serveur illisible. Vérifiez que api/fortisecure_push.php a été envoyé sur le serveur.'); }
      if (!response.ok || !data.success) throw new Error(data.message || 'Création du lien non confirmée.');
      setShareUrl(secretLink(data.id, encrypted.key)); setMessage('Lien créé. Copiez-le pour le transmettre au destinataire.');
    } catch (failure) {
      setError(failure.name === 'AbortError' || failure instanceof TypeError ? 'Réponse non reçue. Un lien a peut-être été créé ; aucun nouvel essai automatique n’a été effectué.' : failure.message);
    } finally { clearTimeout(timeout); shareLock.current = false; setSharing(false); }
  };
  const copyLink = async () => {
    setCopying(true); setMessage('');
    try { await navigator.clipboard.writeText(shareUrl); setMessage('Lien de partage copié.'); }
    catch { linkOutput.current?.focus(); linkOutput.current?.select(); setMessage('Lien sélectionné : utilisez Ctrl+C ou Cmd+C pour le copier.'); }
    finally { setCopying(false); }
  };
  return <section className="password-tool" aria-labelledby="password-heading">
    <div className="password-heading"><span aria-hidden="true">🔐</span><div><h2 id="password-heading">Générateur de mots de passe</h2><p>Un mot de passe différent pour chaque compte.</p></div></div>
    <div className="password-settings">
      <label className="password-length" htmlFor="password-length">Longueur <strong>{length} caractères</strong>
        <input id="password-length" type="range" min="12" max="64" value={length} disabled={busy} onChange={e => {setLength(Number(e.target.value)); clear();}} />
        <span className="password-range"><span>12</span><span>64</span></span>
      </label>
      <fieldset disabled={busy}><legend>Caractères autorisés</legend><div className="password-options">{Object.entries(labels).map(([key,label]) => <label key={key}><input type="checkbox" checked={options[key]} onChange={e => {setOptions({...options,[key]:e.target.checked}); clear();}} />{label}</label>)}</div></fieldset>
    </div>
    {error && <p role="alert" className="password-error">{error}</p>}
    <label className="password-output-label" htmlFor="generated-password">Votre mot de passe</label>
    <p className="password-privacy">Saisissez ou collez un mot de passe existant, ou utilisez le générateur ci-dessus. Jusqu’à 512 caractères.</p>
    <textarea id="generated-password" ref={output} readOnly={busy} rows="2" value={password} maxLength={512} onChange={e => {setPassword(e.target.value); resetShare();}} placeholder="Saisissez votre mot de passe ou générez-en un" spellCheck="false" autoComplete="off" autoCapitalize="off" />
    <div className="password-actions"><button type="button" className="password-generate" onClick={generate} disabled={busy}>{password ? 'Générer un autre mot de passe' : 'Générer un mot de passe'}</button><button type="button" onClick={copy} disabled={!password || busy}>{copying ? 'Copie…' : 'Copier'}</button></div>
    <section className="password-share" aria-labelledby="password-share-heading">
      <h3 id="password-share-heading">Partager avec FortiSecure</h3>
      <p>En cliquant sur « Créer un lien de partage », ce mot de passe sera chiffré dans votre navigateur, puis hébergé temporairement sur fortisecure.fr.</p>
      <div className="password-share-settings">
        <label>Expiration<select value={duration} disabled={busy} onChange={e => {setDuration(Number(e.target.value)); resetShare();}}>
          {[[0,'15 minutes'],[3,'1 heure'],[4,'6 heures'],[6,'1 jour'],[8,'3 jours'],[12,'1 semaine']].map(([value,label]) => <option key={value} value={value}>{label}</option>)}
        </select></label>
        <label>Nombre de consultations<input type="number" min="1" max="100" step="1" value={views} disabled={busy} onChange={e => {setViews(e.target.value); resetShare();}} /></label>
      </div>
      <p className="password-privacy">Le lien expire dès que la durée ou le nombre de consultations est atteint.</p>
      <button type="button" onClick={share} disabled={!password || busy || !!shareUrl || !Number.isInteger(Number(views)) || Number(views) < 1 || Number(views) > 100}>{sharing ? 'Création du lien…' : shareUrl ? 'Lien créé' : 'Créer un lien de partage'}</button>
      {shareUrl && <div className="password-share-result"><label htmlFor="password-share-url">Lien à transmettre</label><input id="password-share-url" ref={linkOutput} readOnly value={shareUrl} autoComplete="off" /><button type="button" onClick={copyLink} disabled={busy}>Copier le lien</button><p className="password-privacy">Créer un autre mot de passe ou changer les réglages ne supprime pas les liens déjà créés.</p></div>}
    </section>
    <p role="status" aria-live="polite" className="password-status">{message}</p>
    <p className="password-privacy">La saisie et la génération restent locales. Le serveur FortiSecure conserve uniquement le contenu chiffré. La clé de déchiffrement reste dans le lien que vous transmettez.</p>
  </section>;
}
